import { useCallback, useMemo } from 'react';
import { useApi, useMutation } from '../../hooks/useApi';
import { getDashboardStats, triggerPax8Sync, triggerGDAPSync } from '../../services/api';
import { DashboardData } from '../../types';
import StatsCards from './StatsCards';
import AlertsPanel from './AlertsPanel';
import RenewalCalendar from './RenewalCalendar';
import DashboardSkeleton from './DashboardSkeleton';
import HelpTooltip from '../common/HelpTooltip';
import { Button } from '../ui/button';
import { RefreshCw, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';

function isEmbedMode(): boolean {
  if (window.location.pathname === '/embedded') return true;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

async function getAnonymousDashboard(): Promise<DashboardData> {
  const response = await fetch('/api/widget-summary?format=dashboard');
  if (!response.ok) throw new Error('Failed to fetch dashboard');
  return response.json();
}

export default function DashboardPage() {
  const embedMode = useMemo(() => isEmbedMode(), []);

  const fetchDashboard = useCallback(
    () => embedMode ? getAnonymousDashboard() : getDashboardStats(),
    [embedMode]
  );
  const { data, loading, error, refetch } = useApi<DashboardData>(fetchDashboard, []);

  const { mutate: syncPax8, loading: syncingPax8 } = useMutation(triggerPax8Sync);
  const { mutate: syncGdap, loading: syncingGdap } = useMutation(triggerGDAPSync);

  const handlePax8Sync = async () => {
    try {
      const result = await syncPax8(undefined as never);
      toast.success(`Pax8 sync completed: ${result.clientsProcessed} clients, ${result.subscriptionsProcessed} subscriptions`);
      refetch();
    } catch {
      toast.error('Pax8 sync failed');
    }
  };

  const handleGdapSync = async () => {
    try {
      const result = await syncGdap(undefined as never);
      toast.success(`GDAP sync completed: ${result.clientsProcessed} clients processed`);
      refetch();
    } catch {
      toast.error('GDAP sync failed');
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-4">
          <AlertTriangle className="h-8 w-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">
          Error loading dashboard
        </h3>
        <p className="text-muted-foreground mt-2 max-w-md mx-auto">{error}</p>
        <Button onClick={refetch} className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Header with Sync Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">
              Dashboard
            </h1>
            <HelpTooltip
              text="This dashboard shows a summary of all your clients, licenses, subscriptions, and alerts. Click any stat card to drill down."
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Overview of your Pax8 licensing status
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {/* Sync Status */}
          {data.syncStatus && (
            <div className="hidden md:flex items-center gap-3 mr-2 text-muted-foreground">
              {data.syncStatus.pax8 && (
                <div className="flex items-center gap-1">
                  {data.syncStatus.pax8.status === 'success' ? (
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-500" />
                  )}
                  <span>Pax8: {new Date(data.syncStatus.pax8.lastSync).toLocaleString()}</span>
                </div>
              )}
              {data.syncStatus.gdap && (
                <div className="flex items-center gap-1">
                  {data.syncStatus.gdap.status === 'success' ? (
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-500" />
                  )}
                  <span>GDAP: {new Date(data.syncStatus.gdap.lastSync).toLocaleString()}</span>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePax8Sync}
              disabled={syncingPax8}
            >
              <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", syncingPax8 && "animate-spin")} />
              {syncingPax8 ? 'Syncing...' : 'Sync Pax8'}
            </Button>
            <HelpTooltip
              text="Syncs subscription data from Pax8. Runs automatically daily."
              size="sm"
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGdapSync}
              disabled={syncingGdap}
            >
              <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", syncingGdap && "animate-spin")} />
              {syncingGdap ? 'Syncing...' : 'Sync GDAP'}
            </Button>
            <HelpTooltip
              text="Syncs license utilization data from Microsoft 365 tenants via GDAP."
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={data.stats} />

      {/* Bento Grid: Alerts (larger) + Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <AlertsPanel alerts={data.recentAlerts} />
        </div>
        <div className="lg:col-span-2">
          <RenewalCalendar renewals={data.upcomingRenewals} />
        </div>
      </div>
    </div>
  );
}
